import React, { useState, useEffect, useMemo } from 'react';
import authService from '../../services/authService';
import { Card } from './../../components/card/card.jsx';
import toastService from '../../services/toastService.js'

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editUser, setEditUser] = useState({
        email: '',
        phone: '',
        organisation: '',
        site: '',
        address: ''
    });
    const [newPassword, setNewPassword] = useState('');
    const [newRole, setNewRole] = useState('');
    const [newUser, setNewUser] = useState({
        username: '',
        password: '',
        role: 'user',
        email: '',
        phone: '',
        organisation: '',
        site: '',
        address: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('username');
    const [sortDirection, setSortDirection] = useState('asc');
    const [actionBusyId, setActionBusyId] = useState(null);
    const [mfaFilter, setMfaFilter] = useState('all'); // all | enabled | disabled
    // New: filters for Role & Organisation
    const [roleFilter, setRoleFilter] = useState('all'); // all | admin | superuser | user
    const [orgFilter, setOrgFilter] = useState('all'); // all | specific organisation
    const [loginAttempts, setLoginAttempts] = useState([]);
    const [lastLoginMap, setLastLoginMap] = useState({}); // username -> { ip, timestamp }
    const [showLoginHistoryModal, setShowLoginHistoryModal] = useState(false);
    const [historyUsername, setHistoryUsername] = useState('');
    const [historyLimit, setHistoryLimit] = useState(50);
    const [historyOffset, setHistoryOffset] = useState(0);
    const [historyItems, setHistoryItems] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [historyHasMore, setHistoryHasMore] = useState(false);
    const [historyError, setHistoryError] = useState('');
    // Tabs & Organizations management state
    const [activeTab, setActiveTab] = useState('users');
    const [orgSearch, setOrgSearch] = useState('');
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showClearModal, setShowClearModal] = useState(false);
    const [orgToRename, setOrgToRename] = useState('');
    const [orgToMove, setOrgToMove] = useState('');
    const [renameTarget, setRenameTarget] = useState('');
    const [moveTarget, setMoveTarget] = useState('');
    const [orgActionBusy, setOrgActionBusy] = useState(false);
    // New: Add Organization modal and extra organizations (for 0-user orgs)
    const [showAddOrgModal, setShowAddOrgModal] = useState(false);
    const [newOrgName, setNewOrgName] = useState('');
    const [extraOrgs, setExtraOrgs] = useState([]);

    // Persist extraOrgs in localStorage
    useEffect(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('extraOrgs') || '[]');
            if (Array.isArray(saved)) {
                setExtraOrgs(saved.filter(Boolean));
            }
        } catch (e) {
            console.error('Failed to load extraOrgs', e);
        }
    }, []);
    useEffect(() => {
        try {
            localStorage.setItem('extraOrgs', JSON.stringify(extraOrgs));
        } catch (e) {
            console.error('Failed to save extraOrgs', e);
        }
    }, [extraOrgs]);

    // Load login history for selected user when modal opens or params change
    useEffect(() => {
        if (!showLoginHistoryModal || !historyUsername) return;
        const load = async () => {
            setLoadingHistory(true);
            setHistoryError('');
            try {
                const data = await authService.getLoginAttempts({
                    username: historyUsername,
                    limit: historyLimit,
                    offset: historyOffset
                });
                const items = (data && (data.attempts || data.items || data.data)) || [];
                setHistoryItems(items);
                setHistoryHasMore(items.length === historyLimit);
            } catch (e) {
                setHistoryError(e?.message || 'Failed to load login history');
                setHistoryItems([]);
                setHistoryHasMore(false);
            } finally {
                setLoadingHistory(false);
            }
        };
        load();
    }, [showLoginHistoryModal, historyUsername, historyLimit, historyOffset]);
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const userList = await authService.getAllUsers();
            setUsers(userList);
        } catch (error) {
            setError('Failed to fetch users.');
        } finally {
            setLoading(false);
        }
    };

    // New: fetch login attempts and compute last successful login per user
    const fetchLoginAttempts = async () => {
        try {
            const data = await authService.getLoginAttempts({ limit: 1000, offset: 0 });
            const arr = (data && (data.attempts || data.data)) || [];
            setLoginAttempts(arr);
            const map = {};
            arr.forEach(a => {
                if (!a || !a.username || !a.success) return;
                const ts = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                const prev = map[a.username]?.ts || 0;
                if (ts >= prev) {
                    map[a.username] = { ip: a.ip_address || '-', timestamp: a.timestamp || null, ts };
                }
            });
            setLastLoginMap(map);
        } catch (e) {
            console.error('Failed to fetch login attempts', e);
        }
    };

    // Initial load: fetch users and login attempts
    useEffect(() => {
        fetchUsers();
        fetchLoginAttempts();
    }, []);

    // Sorting function
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Compute unique organisations for filter options
    const uniqueOrgs = Array.from(new Set((users || []).map(u => u?.organisation).filter(Boolean))).sort();

    // New: aggregate organizations statistics
    const orgStats = useMemo(() => {
        const map = {};
        (users || []).forEach(u => {
            const name = u?.organisation || '';
            const site = u?.site || '';
            if (!map[name]) map[name] = { name, count: 0, sites: new Set() };
            map[name].count += 1;
            if (site) map[name].sites.add(site);
        });
        return Object.values(map)
            .map(o => ({ ...o, sites: Array.from(o.sites).sort() }))
            .sort((a, b) => (a.name || '').toString().localeCompare((b.name || '').toString()));
    }, [users]);

    // New: all organization names incl. extra empty orgs
    const allOrgNames = useMemo(() => Array.from(new Set([
        ...uniqueOrgs,
        ...extraOrgs.filter(Boolean)
    ])).sort(), [uniqueOrgs, extraOrgs]);

    // New: display list merges orgStats with extraOrgs (0 users)
    const orgDisplay = useMemo(() => {
        const base = orgStats.map(o => ({ ...o }));
        const exist = new Set(base.map(o => o.name || ''));
        extraOrgs.filter(Boolean).forEach(name => {
            if (!exist.has(name)) base.push({ name, count: 0, sites: [] });
        });
        return base.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [orgStats, extraOrgs]);

    // Helper to format timestamps
    const formatDateTime = (ts) => {
        if (!ts) return '-';
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) return '-';
        return d.toLocaleString();
    };

    // Filter and sort users
    const filteredAndSortedUsers = users
        .filter(user => {
            // Role filter
            if (roleFilter !== 'all' && (user.role || '').toLowerCase() !== roleFilter) return false;
            // Organisation filter
            if (orgFilter !== 'all' && (user.organisation || '') !== orgFilter) return false;
            // MFA filter
            if (mfaFilter === 'enabled' && !user.mfa_enabled) return false;
            if (mfaFilter === 'disabled' && user.mfa_enabled) return false;

            // Search
            const searchLower = searchTerm.toLowerCase();
            return (
                user.username?.toLowerCase().includes(searchLower) ||
                user.role?.toLowerCase().includes(searchLower) ||
                user.email?.toLowerCase().includes(searchLower) ||
                user.phone?.toLowerCase().includes(searchLower) ||
                user.organisation?.toLowerCase().includes(searchLower) ||
                user.site?.toLowerCase().includes(searchLower)
            );
        })
        .sort((a, b) => {
            const aValue = a[sortField] || '';
            const bValue = b[sortField] || '';
            const comparison = aValue.toString().localeCompare(bValue.toString());
            return sortDirection === 'asc' ? comparison : -comparison;
        });

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            setLoading(true);
            try {
                await authService.deleteUser(userId);
                fetchUsers(); // Refresh the user list
            } catch (error) {
                setError('Failed to delete user.');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleUpdatePassword = async () => {
        if (!newPassword) {
            setError('Password cannot be empty.');
            return;
        }
        setLoading(true);
        try {
            await authService.updateUserPassword(selectedUser.id, newPassword);
            setShowPasswordModal(false);
            setNewPassword('');
            setSuccess('Password updated successfully.');
        } catch (error) {
            setError('Failed to update password.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateRole = async () => {
        if (!newRole) {
            setError('Role cannot be empty.');
            return;
        }
        setLoading(true);
        try {
            await authService.updateUserRole(selectedUser.id, newRole);
            setShowRoleModal(false);
            setNewRole('');
            setSuccess('Role updated successfully.');
            fetchUsers(); // Refresh the user list to show updated role
        } catch (error) {
            setError('Failed to update role.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewUser(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditUser(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setEditUser({
            email: user.email || '',
            phone: user.phone || '',
            organisation: user.organisation || '',
            site: user.site || '',
            address: user.address || ''
        });
        setShowEditModal(true);
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const result = await authService.updateUserProfile(
                selectedUser.id,
                editUser.email,
                editUser.phone,
                editUser.organisation,
                editUser.site,
                editUser.address
            );

            if (result.success) {
                setSuccess(result.message);
                setShowEditModal(false);
                fetchUsers(); // Refresh the user list
            }
        } catch (error) {
            setError(error.message || 'Failed to update user profile.');
        } finally {
            setLoading(false);
        }
    };

    // Reset MFA handler
    const handleResetMfa = async (user) => {
        if (!window.confirm(`Reset MFA for user "${user.username}"? This will require them to set up MFA again on next login.`)) {
            return;
        }
        setActionBusyId(user.id);
        setError('');
        try {
            const res = await authService.resetUserMfa(user.id);
            if (res && (res.success || res.status === 'ok')) {
                setSuccess('MFA has been reset successfully.');
                toastService.success(`MFA reset for ${user.username}`);
                await fetchUsers();
            } else {
                const msg = (res && res.message) || 'Failed to reset MFA';
                setError(msg);
                toastService.error(msg);
            }
        } catch (e) {
            const msg = e.message || 'Failed to reset MFA';
            setError(msg);
            toastService.error(msg);
        } finally {
            setActionBusyId(null);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const result = await authService.createUser(
                newUser.username,
                newUser.password,
                newUser.role,
                newUser.email,
                newUser.phone,
                newUser.organisation,
                newUser.site,
                newUser.address
            );

            if (result.success) {
                setSuccess(result.message);
                setNewUser({ 
                    username: '', 
                    password: '', 
                    role: 'user',
                    email: '',
                    phone: '',
                    organisation: '',
                    site: '',
                    address: ''
                });
                setShowCreateForm(false);
                fetchUsers(); // Refresh the user list
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Failed to create user. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Organizations management actions
    const renameOrgConfirm = async (e) => {
        e.preventDefault();
        const from = (orgToRename || '').trim();
        const to = (renameTarget || '').trim();
        if (!to) {
            toastService.error('Please enter a new organization name');
            return;
        }
        if (from === to) {
            toastService.info('Organization name unchanged');
            setShowRenameModal(false);
            return;
        }
        const affected = (users || []).filter(u => (u.organisation || '') === from);
        if (affected.length === 0) {
            toastService.info('No users to update for this organization');
            setShowRenameModal(false);
            return;
        }
        setOrgActionBusy(true);
        try {
            for (const u of affected) {
                await authService.updateUserProfile(
                    u.id,
                    u.email || '',
                    u.phone || '',
                    to,
                    u.site || '',
                    u.address || ''
                );
            }
            toastService.success(`Renamed organization${from ? ` "${from}"` : ''} to "${to}" for ${affected.length} user(s)`);
            setShowRenameModal(false);
            setOrgToRename('');
            setRenameTarget('');
            await fetchUsers();
        } catch (err) {
            toastService.error(err?.message || 'Failed to rename organization');
        } finally {
            setOrgActionBusy(false);
        }
    };

    const moveOrgConfirm = async (e) => {
        e.preventDefault();
        const from = (orgToMove || '').trim();
        const to = (moveTarget || '').trim();
        if (!to) {
            toastService.error('Please enter the target organization');
            return;
        }
        if (from === to) {
            toastService.info('Source and target organizations are the same');
            setShowMoveModal(false);
            return;
        }
        const affected = (users || []).filter(u => (u.organisation || '') === from);
        if (affected.length === 0) {
            toastService.info('No users to move for this organization');
            setShowMoveModal(false);
            return;
        }
        setOrgActionBusy(true);
        try {
            for (const u of affected) {
                await authService.updateUserProfile(
                    u.id,
                    u.email || '',
                    u.phone || '',
                    to,
                    u.site || '',
                    u.address || ''
                );
            }
            toastService.success(`Moved ${affected.length} user(s) from ${from || 'Unassigned'} to ${to}`);
            setShowMoveModal(false);
            setOrgToMove('');
            setMoveTarget('');
            await fetchUsers();
        } catch (err) {
            toastService.error(err?.message || 'Failed to move users');
        } finally {
            setOrgActionBusy(false);
        }
    };

    const clearOrgConfirm = async () => {
        const from = (orgToRename || '').trim();
        const affected = (users || []).filter(u => (u.organisation || '') === from);
        if (affected.length === 0) {
            toastService.info('No users to clear for this organization');
            setShowClearModal(false);
            return;
        }
        setOrgActionBusy(true);
        try {
            for (const u of affected) {
                await authService.updateUserProfile(
                    u.id,
                    u.email || '',
                    u.phone || '',
                    '',
                    u.site || '',
                    u.address || ''
                );
            }
            toastService.success(`Cleared organization for ${affected.length} user(s)`);
            setShowClearModal(false);
            setOrgToRename('');
            await fetchUsers();
        } catch (err) {
            toastService.error(err?.message || 'Failed to clear organization');
        } finally {
            setOrgActionBusy(false);
        }
    };

    // Add Organization handler
    const addOrgConfirm = (e) => {
        e.preventDefault();
        const name = (newOrgName || '').trim();
        if (!name) {
            toastService.error('Please enter organization name');
            return;
        }
        const exists = allOrgNames.some(n => (n || '').toLowerCase() === name.toLowerCase());
        if (exists) {
            toastService.info('Organization already exists');
            setShowAddOrgModal(false);
            setNewOrgName('');
            return;
        }
        setExtraOrgs(prev => {
            const set = new Set(prev.map(v => (v || '').trim()).filter(Boolean));
            set.add(name);
            return Array.from(set).sort((a, b) => a.localeCompare(b));
        });
        toastService.success(`Organization "${name}" added`);
        setShowAddOrgModal(false);
        setNewOrgName('');
    };

    return (
        <>
            <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card className="mb-4" style={{ zIndex: '1', height: '78vh' }}>
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h4 className="card-title mb-0">User Management</h4>
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowCreateForm(!showCreateForm)}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Create New User
                            </button>
                        </div>
                        {/* Alerts */}
                        {success && (
                            <div className="alert alert-success mx-3 my-2" role="alert">
                                {success}
                            </div>
                        )}
                        {error && (
                            <div className="alert alert-danger mx-3 my-2" role="alert">
                                {error}
                            </div>
                        )}

                        <div className="card-body">
                            {/* Tabs */}
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button className={`nav-link ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>Users</button>
                                </li>
                                <li className="nav-item">
                                    <button className={`nav-link ${activeTab === 'organizations' ? 'active' : ''}`} onClick={() => setActiveTab('organizations')}>Organizations</button>
                                </li>
                            </ul>

                            {activeTab === 'users' && (
                                <>
                                {/* Create User Form */}
                                {showCreateForm && (
                                    <div className="mb-4">
                                        <h5>Create New User</h5>
                                        <form onSubmit={handleCreateUser}>
                                            <div className="row g-3">
                                                <div className="col-md-3">
                                                    <label className="form-label">Username</label>
                                                    <input type="text" className="form-control" name="username" value={newUser.username} onChange={handleInputChange} required />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">Password</label>
                                                    <input type="password" className="form-control" name="password" value={newUser.password} onChange={handleInputChange} required />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">Role</label>
                                                    <select className="form-select" name="role" value={newUser.role} onChange={handleInputChange}>
                                                        <option value="admin">Admin</option>
                                                        <option value="superuser">Superuser</option>
                                                        <option value="user">User</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">Organisation</label>
                                                    <input type="text" className="form-control" name="organisation" value={newUser.organisation} onChange={handleInputChange} placeholder="e.g. ACME Corp" />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label">Email</label>
                                                    <input type="email" className="form-control" name="email" value={newUser.email} onChange={handleInputChange} />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label">Phone</label>
                                                    <input type="text" className="form-control" name="phone" value={newUser.phone} onChange={handleInputChange} />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label">Site</label>
                                                    <input type="text" className="form-control" name="site" value={newUser.site} onChange={handleInputChange} placeholder="e.g. London" />
                                                </div>
                                                <div className="col-12">
                                                    <label className="form-label">Address</label>
                                                    <textarea className="form-control" name="address" rows="2" value={newUser.address} onChange={handleInputChange} />
                                                </div>
                                            </div>
                                            <div className="mt-3 d-flex gap-2">
                                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                                    {loading ? 'Creating...' : 'Create User'}
                                                </button>
                                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateForm(false)}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                )}

                                {/* User List */}
                                <div>
                                    <h5 className="mb-3">User List</h5>
                                    <div className="row g-2 mb-3 align-items-end">
                                        <div className="col-md-4">
                                            <label className="form-label">Search</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search users..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label">Role</label>
                                            <select className="form-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                                                <option value="all">All Roles</option>
                                                <option value="admin">Admin</option>
                                                <option value="superuser">Superuser</option>
                                                <option value="user">User</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">Organisation</label>
                                            <select className="form-select" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
                                                <option value="all">All Organisations</option>
                                                {uniqueOrgs.map(org => (
                                                    <option key={org} value={org}>{org}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">MFA</label>
                                            <select className="form-select" value={mfaFilter} onChange={(e) => setMfaFilter(e.target.value)}>
                                                <option value="all">All</option>
                                                <option value="enabled">Enabled</option>
                                                <option value="disabled">Disabled</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="table-responsive" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                                        <table className="table table-striped align-middle">
                                            <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                                <tr>
                                                    <th style={{cursor:'pointer'}} onClick={() => handleSort('username')}>Username {sortField==='username' && (sortDirection==='asc' ? '▲' : '▼')}</th>
                                                    <th style={{cursor:'pointer'}} onClick={() => handleSort('role')}>Role {sortField==='role' && (sortDirection==='asc' ? '▲' : '▼')}</th>
                                                    <th style={{cursor:'pointer'}} onClick={() => handleSort('email')}>Email {sortField==='email' && (sortDirection==='asc' ? '▲' : '▼')}</th>
                                                    <th style={{cursor:'pointer'}} onClick={() => handleSort('phone')}>Phone {sortField==='phone' && (sortDirection==='asc' ? '▲' : '▼')}</th>
                                                    <th style={{cursor:'pointer'}} onClick={() => handleSort('organisation')}>Organisation {sortField==='organisation' && (sortDirection==='asc' ? '▲' : '▼')}</th>
                                                    <th>Last Login</th>
                                                    <th>MFA</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan="8" className="text-center py-4">Loading users...</td></tr>
                                                ) : filteredAndSortedUsers.length === 0 ? (
                                                    <tr><td colSpan="8" className="text-center py-4">No users found.</td></tr>
                                                ) : (
                                                    filteredAndSortedUsers.map(user => {
                                                        const last = lastLoginMap[user.username] || null;
                                                        return (
                                                            <tr key={user.id}>
                                                                <td style={{whiteSpace:'nowrap'}}>{user.username}</td>
                                                                <td>{user.role}</td>
                                                                <td>{user.email || '-'}</td>
                                                                <td>{user.phone || '-'}</td>
                                                                <td>{user.organisation || '-'}</td>
                                                                <td style={{whiteSpace:'nowrap'}}>
                                                                    {last ? (
                                                                        <>
                                                                            <div title={`IP: ${last.ip || '-'}`}>{formatDateTime(last.timestamp)}</div>
                                                                            <small className="text-muted">{last.ip || '-'}</small>
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-muted">-</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span className={`badge ${user.mfa_enabled ? 'bg-success' : 'bg-secondary'}`}>
                                                                        {user.mfa_enabled ? 'Enabled' : 'Disabled'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="dropdown">
                                                                        <button className="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" title="Actions">
                                                                            <i className="bi bi-three-dots-vertical"></i>
                                                                        </button>
                                                                        <ul className="dropdown-menu dropdown-menu-end">
                                                                            <li>
                                                                                <button className="dropdown-item" onClick={() => { setSelectedUser(user); setShowPasswordModal(true); }}>
                                                                                    <i className="bi bi-key me-2"></i>
                                                                                    Change Password
                                                                                </button>
                                                                            </li>
                                                                            <li>
                                                                                <button className="dropdown-item" onClick={() => { setSelectedUser(user); setShowRoleModal(true); }}>
                                                                                    <i className="bi bi-person-gear me-2"></i>
                                                                                    Change Role
                                                                                </button>
                                                                            </li>
                                                                            <li>
                                                                                <button className="dropdown-item" onClick={() => handleEditUser(user)}>
                                                                                    <i className="bi bi-pencil-square me-2"></i>
                                                                                    Edit Profile
                                                                                </button>
                                                                            </li>
                                                                            <li>
                                                                                <button className="dropdown-item" onClick={() => { setHistoryUsername(user.username); setShowLoginHistoryModal(true); }}>
                                                                                    <i className="bi bi-clock-history me-2"></i>
                                                                                    View Logins
                                                                                </button>
                                                                            </li>
                                                                            <li>
                                                                                <button className="dropdown-item" disabled={actionBusyId===user.id} onClick={() => handleResetMfa(user)}>
                                                                                    <i className="bi bi-shield-lock me-2"></i>
                                                                                    {actionBusyId===user.id ? 'Resetting MFA...' : 'Reset MFA'}
                                                                                </button>
                                                                            </li>
                                                                            <li><hr className="dropdown-divider" /></li>
                                                                            <li>
                                                                                <button className="dropdown-item text-danger" onClick={() => handleDeleteUser(user.id)}>
                                                                                    <i className="bi bi-trash me-2"></i>
                                                                                    Delete User
                                                                                </button>
                                                                            </li>
                                                                        </ul>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                </>
                            )}

                            {activeTab === 'organizations' && (
                                <div>
                                    <div className="row mb-3 g-2">
                                        <div className="col-md-4">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search organizations..."
                                                value={orgSearch}
                                                onChange={(e) => setOrgSearch(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-8 d-flex align-items-center justify-content-end gap-2">
                                            <button className="btn btn-success btn-sm" onClick={() => setShowAddOrgModal(true)}>
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Organization
                                            </button>
                                            <small className="text-muted">Total organizations: {orgDisplay.length}</small>
                                        </div>
                                    </div>

                                    <div className="table-responsive" style={{ minHeight: '60vh', overflowY: 'auto' }}>
                                        <table className="table table-striped table-hover">
                                            <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                                <tr>
                                                    <th>Organization</th>
                                                    <th style={{whiteSpace:'nowrap'}}>Users</th>
                                                    <th>Sites</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orgDisplay
                                                    .filter(o => {
                                                        const label = o.name || 'Unassigned';
                                                        return label.toLowerCase().includes(orgSearch.toLowerCase());
                                                    })
                                                    .map((o, idx) => (
                                                    <tr key={`${o.name}-${idx}`}>
                                                        <td>{o.name || <span className="text-muted">Unassigned</span>}</td>
                                                        <td>{o.count}</td>
                                                        <td className="text-truncate" style={{maxWidth:'360px'}}>
                                                            {(o.sites || []).length ? o.sites.join(', ') : <span className="text-muted">-</span>}
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm" role="group">
                                                                <button className="btn btn-outline-primary" title="Rename" onClick={() => { setOrgToRename(o.name || ''); setRenameTarget(o.name || ''); setShowRenameModal(true); }}>
                                                                    <i className="bi bi-pencil-square me-1"></i>
                                                                    Rename
                                                                </button>
                                                                <button className="btn btn-outline-secondary" title="Move Users" onClick={() => { setOrgToMove(o.name || ''); setMoveTarget(''); setShowMoveModal(true); }}>
                                                                    <i className="bi bi-arrow-left-right me-1"></i>
                                                                    Move Users
                                                                </button>
                                                                <button className="btn btn-outline-danger" title="Clear" disabled={o.count === 0} onClick={() => { setOrgToRename(o.name || ''); setShowClearModal(true); }}>
                                                                    <i className="bi bi-trash me-1"></i>
                                                                    Clear
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {orgDisplay.length === 0 && (
                                                    <tr>
                                                        <td colSpan="4" className="text-center">No organizations found.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        </div>

            {/* Change Password Modal */}
            {showPasswordModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Change Password for {selectedUser?.username}</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setNewPassword('');
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="newPassword" className="form-label">New Password</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        id="newPassword"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Enter new password"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setNewPassword('');
                                    }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary" 
                                    onClick={handleUpdatePassword}
                                    disabled={loading}
                                >
                                    {loading ? 'Updating...' : 'Update Password'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Role Modal */}
            {showRoleModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Change Role for {selectedUser?.username}</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => {
                                        setShowRoleModal(false);
                                        setNewRole('');
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="newRole" className="form-label">Role</label>
                                    <select
                                        className="form-select"
                                        id="newRole"
                                        value={newRole}
                                        onChange={(e) => setNewRole(e.target.value)}
                                    >
                                        <option value="">Select a role</option>
                                        <option value="admin">Admin</option>
                                        <option value="superuser">Superuser</option>
                                        <option value="user">User</option>
                                        
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => {
                                        setShowRoleModal(false);
                                        setNewRole('');
                                    }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary" 
                                    onClick={handleUpdateRole}
                                    disabled={loading}
                                >
                                    {loading ? 'Updating...' : 'Update Role'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {showEditModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit User: {selectedUser?.username}</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setEditUser({
                                            email: '',
                                            phone: '',
                                            organisation: '',
                                            site: '',
                                            address: ''
                                        });
                                    }}
                                ></button>
                            </div>
                            <form onSubmit={handleUpdateUser}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="editEmail" className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="editEmail"
                                            name="email"
                                            value={editUser.email}
                                            onChange={handleEditInputChange}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editPhone" className="form-label">Phone</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="editPhone"
                                            name="phone"
                                            value={editUser.phone}
                                            onChange={handleEditInputChange}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editOrganisation" className="form-label">Organisation</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="editOrganisation"
                                            name="organisation"
                                            value={editUser.organisation}
                                            onChange={handleEditInputChange}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editSite" className="form-label">Site</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="editSite"
                                            name="site"
                                            value={editUser.site}
                                            onChange={handleEditInputChange}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editAddress" className="form-label">Address</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="editAddress"
                                            name="address"
                                            value={editUser.address}
                                            onChange={handleEditInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => {
                                            setShowEditModal(false);
                                            setEditUser({
                                                email: '',
                                                phone: '',
                                                organisation: '',
                                                site: '',
                                                address: ''
                                            });
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? 'Updating...' : 'Update User'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Login History Modal */}
            {showLoginHistoryModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Login History: {historyUsername}</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowLoginHistoryModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <label className="form-label me-2">Limit</label>
                                        <select 
                                            className="form-select d-inline-block w-auto"
                                            value={historyLimit}
                                            onChange={(e) => { setHistoryLimit(parseInt(e.target.value, 10)); setHistoryOffset(0); }}
                                        >
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setHistoryOffset(Math.max(0, historyOffset - historyLimit))}
                                            disabled={historyOffset === 0 || loadingHistory}
                                        >
                                            <i className="bi bi-chevron-left me-1"></i>
                                            Prev
                                        </button>
                                        <span className="text-muted small">
                                            Page {Math.floor(historyOffset / historyLimit) + 1}
                                        </span>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setHistoryOffset(historyOffset + historyLimit)}
                                            disabled={!historyHasMore || loadingHistory}
                                        >
                                            Next
                                            <i className="bi bi-chevron-right ms-1"></i>
                                        </button>
                                    </div>
                                </div>

                                {historyError && (
                                    <div className="alert alert-danger">{historyError}</div>
                                )}

                                {loadingHistory ? (
                                    <div className="text-center py-4">Loading history...</div>
                                ) : historyItems.length === 0 ? (
                                    <div className="alert alert-info">No login attempts to display.</div>
                                ) : (
                                    <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                        <table className="table table-sm table-striped align-middle">
                                            <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                                <tr>
                                                    <th style={{whiteSpace:'nowrap'}}>Timestamp</th>
                                                    <th>IP</th>
                                                    <th>User Agent</th>
                                                    <th>Status</th>
                                                    <th>Message</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historyItems.map((a, idx) => (
                                                    <tr key={`${a.id || a.timestamp || idx}-${idx}`}>
                                                        <td style={{whiteSpace:'nowrap'}}>{a.timestamp ? new Date(a.timestamp).toLocaleString() : '-'}</td>
                                                        <td>{a.ip_address || a.ip || '-'}</td>
                                                        <td className="text-truncate" style={{maxWidth:'420px'}} title={a.user_agent || a.ua || ''}>{a.user_agent || a.ua || '-'}</td>
                                                        <td>
                                                            <span className={`badge ${a.success ? 'bg-success' : 'bg-danger'}`}>
                                                                {a.success ? 'Success' : 'Failed'}
                                                            </span>
                                                        </td>
                                                        <td className="text-truncate" style={{maxWidth:'320px'}} title={a.message || a.reason || ''}>{a.message || a.reason || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => setShowLoginHistoryModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Organizations: Rename Modal */}
            {activeTab === 'organizations' && showRenameModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Rename Organization</h5>
                                <button type="button" className="btn-close" onClick={() => setShowRenameModal(false)}></button>
                            </div>
                            <form onSubmit={renameOrgConfirm}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Current Name</label>
                                        <input type="text" className="form-control" value={orgToRename || 'Unassigned'} readOnly />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">New Name</label>
                                        <input type="text" className="form-control" value={renameTarget} onChange={(e) => setRenameTarget(e.target.value)} required />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowRenameModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={orgActionBusy}>{orgActionBusy ? 'Renaming...' : 'Rename'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Organizations: Move Users Modal */}
            {activeTab === 'organizations' && showMoveModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Move Users to Organization</h5>
                                <button type="button" className="btn-close" onClick={() => setShowMoveModal(false)}></button>
                            </div>
                            <form onSubmit={moveOrgConfirm}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">From</label>
                                        <input type="text" className="form-control" value={orgToMove || 'Unassigned'} readOnly />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">To Organization</label>
                                        <input list="orgList" type="text" className="form-control" placeholder="Choose from list or type a new name" value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} required />
                                        <datalist id="orgList">
                                            {allOrgNames.map(name => (
                                                <option key={name} value={name}></option>
                                            ))}
                                        </datalist>
                                        <div className="form-text">Pick an existing organization or enter a new one to create it implicitly.</div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowMoveModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={orgActionBusy}>{orgActionBusy ? 'Moving...' : 'Move'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Organizations: Clear Org Modal */}
            {activeTab === 'organizations' && showClearModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Clear Organization</h5>
                                <button type="button" className="btn-close" onClick={() => setShowClearModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to clear the organization for all users in <strong>{orgToRename || 'Unassigned'}</strong>?</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowClearModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-danger" onClick={clearOrgConfirm} disabled={orgActionBusy}>{orgActionBusy ? 'Clearing...' : 'Clear'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Organizations: Add Organization Modal */}
            {activeTab === 'organizations' && showAddOrgModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Organization</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddOrgModal(false)}></button>
                            </div>
                            <form onSubmit={addOrgConfirm}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Organization Name</label>
                                        <input type="text" className="form-control" value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} placeholder="e.g. ACME Corp" required />
                                        <div className="form-text">This will appear in the list with 0 users until you move or create users under it.</div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowAddOrgModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary">Add</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default UserManagement;